import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Collapse, Table, Tag, Tooltip, Typography, Input, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

const { Text } = Typography;

const REGISTRY_TYPE_LABELS = {
    customer_order: 'Заказ покупателя',
    customer_act: 'Акт покупателя',
    supplier_order: 'Заказ поставщика',
    supplier_act: 'Акт поставщика',
    daily_report: 'Ежедневный отчёт'
};

const EVENT_LABELS = {
    created: 'при создании документа',
    items_added: 'добавлена в документ',
    auto_added: 'автоматически из заказа поставщика',
    task_removed: 'заявка убрана из документа',
    item_removed: 'строка убрана из документа',
    auto_removed: 'автоматически при изменении заказа поставщика',
    invoice_deleted: 'строка удалена из ВДР',
    registry_deleted: 'документ удалён'
};

const money = value =>
    Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const registryUrl = (type, id) =>
    type === 'daily_report' ? `/daily-reports?id=${id}` : `/docs?id=${id}`;

const RegistryLink = ({ row }) => {
    const label = `${REGISTRY_TYPE_LABELS[row.registry_type] || 'Документ'} №${row.registry_number || row.registry_id}`;
    if (row.registry_exists === 0 || row.registry_exists === false) {
        return (
            <Text type="secondary" delete>
                {label} (удалён)
            </Text>
        );
    }
    return (
        <Typography.Link href={registryUrl(row.registry_type, row.registry_id)} target="_blank" rel="noopener noreferrer">
            {label} <LinkOutlined style={{ fontSize: 11 }} />
        </Typography.Link>
    );
};

const columns = [
    {
        title: 'Когда',
        dataIndex: 'created_at',
        width: 130,
        render: (value, row) =>
            row.untracked ? (
                <Tooltip title={`Привязка сделана до ведения журнала. Документ создан ${row.registry_created_at ? dayjs(row.registry_created_at).format('DD.MM.YYYY HH:mm') : '—'}`}>
                    <Text type="secondary">до журнала</Text>
                </Tooltip>
            ) : (
                dayjs(value).format('DD.MM.YYYY HH:mm')
            )
    },
    {
        title: 'Кто',
        dataIndex: 'user_fio',
        width: 190,
        render: (value, row) =>
            row.untracked ? (
                <Tooltip title="Автор документа">
                    <Text type="secondary">{row.registry_author || '—'}</Text>
                </Tooltip>
            ) : (
                value || (row.user_id ? `ID ${row.user_id}` : 'система')
            )
    },
    {
        title: 'Действие',
        dataIndex: 'action',
        width: 130,
        render: (value, row) =>
            row.untracked ? (
                <Tag color="default">В документе</Tag>
            ) : value === 'linked' ? (
                <Tag color="green">Привязана</Tag>
            ) : (
                <Tag color="red">Отвязана</Tag>
            )
    },
    {
        title: 'Строка ВДР',
        dataIndex: 'rate_name',
        ellipsis: { showTitle: true },
        render: (value, row) => (
            <span>
                {value || row.article || '—'}
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {` · ID ${row.invoice_id}${row.amount !== null && row.amount !== undefined ? ` · ${money(row.amount)} ₽` : ''}`}
                </Text>
            </span>
        )
    },
    {
        title: 'Документ',
        key: 'registry',
        width: 240,
        render: (_, row) => <RegistryLink row={row} />
    },
    {
        title: 'Как',
        dataIndex: 'event',
        width: 230,
        render: (value, row) => (
            <Text type="secondary">
                {row.untracked ? 'привязана до ведения журнала' : EVENT_LABELS[value] || '—'}
            </Text>
        )
    }
];

/**
 * История привязки строк ВДР заявки к документам (заказы, акты, ежедневные отчёты).
 * reloadKey — любое значение, при смене которого история перечитывается.
 */
const InvoiceLinkHistoryPanel = ({ taskId, reloadKey }) => {
    const [data, setData] = useState({ history: [], untracked: [] });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!taskId) return undefined;
        let cancelled = false;
        setLoading(true);
        axios
            .get('/api/registries/invoice-link-history', {
                params: { task_id: taskId },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })
            .then(res => {
                if (!cancelled) {
                    setData({
                        history: res.data?.history || [],
                        untracked: res.data?.untracked || []
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setData({ history: [], untracked: [] });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [taskId, reloadKey]);

    const rows = useMemo(() => {
        const all = [
            ...data.history.map(row => ({ ...row, key: `h${row.id}` })),
            ...data.untracked.map(row => ({
                ...row,
                key: `u${row.invoice_id}-${row.registry_id}`,
                untracked: true
            }))
        ];
        const query = search.trim().toLowerCase();
        if (!query) return all;
        return all.filter(row =>
            [row.rate_name, row.article, row.invoice_id, row.registry_number, row.user_fio, row.registry_author]
                .map(value => String(value ?? '').toLowerCase())
                .some(value => value.includes(query))
        );
    }, [data, search]);

    const total = data.history.length + data.untracked.length;

    return (
        <Collapse
            style={{ marginTop: 16 }}
            items={[
                {
                    key: 'links',
                    label: (
                        <Space>
                            <b>История привязок строк ВДР к документам</b>
                            <Tag>{total}</Tag>
                        </Space>
                    ),
                    children: (
                        <>
                            <Input.Search
                                allowClear
                                placeholder="Поиск: строка, ID, № документа, сотрудник"
                                style={{ maxWidth: 380, marginBottom: 8 }}
                                onChange={event => setSearch(event.target.value)}
                            />
                            <Table
                                size="small"
                                rowKey="key"
                                loading={loading}
                                columns={columns}
                                dataSource={rows}
                                pagination={rows.length > 15 ? { pageSize: 15, showSizeChanger: false } : false}
                                locale={{ emptyText: 'Строки ВДР этой заявки ещё не привязывались к документам' }}
                            />
                        </>
                    )
                }
            ]}
        />
    );
};

export default InvoiceLinkHistoryPanel;
